// The Avengers Base catalog — Phase 4's coin sink (docs/02 "reward economy").
//
// He spends Hero Coins (minted ONLY by decoding) to build & decorate a base that grows
// over weeks. A cutaway "dollhouse" tower of zones; buying an item auto-places it in its
// slot. Guardrails: every price is fixed and shown (no loot boxes), nothing here is a
// reading shortcut, no lives/timers. Cadence is tuned "frequent early → ramps" so the
// first builds land in early sessions and showpieces take a few.
//
// Art is emoji for v1 behind `HqItem.art` (a plain string). Real chibi art is a drop-in
// later: generate via the Pixel Lab seam (see `.mcp.example.json` + `src/assets/hq/
// manifest.json`), bundle PNGs into `public/hq/<id>.png`, and swap `art` "🛋️" → "/hq/<id>.png".
// The mechanic never inspects the art's shape, so that swap is data-only.

export type HqZone = 'lobby' | 'gym' | 'lab' | 'garage' | 'roof';

export interface HqZoneDef {
  id: HqZone;
  name: string;
}

/** Tower render order, roof at the top down to the lobby (a base cross-section). */
export const ZONES: HqZoneDef[] = [
  { id: 'roof', name: 'Roof' },
  { id: 'garage', name: 'Garage' },
  { id: 'lab', name: 'Lab' },
  { id: 'gym', name: 'Training' },
  { id: 'lobby', name: 'Lobby' },
];

export interface HqItem {
  id: string;
  name: string;
  zone: HqZone;
  /** Emoji now; an image URL ("/hq/<id>.png") once real art is generated. */
  art: string;
  cost: number;
  /** Tie-break ordering within equal costs (drives the default "next goal"). */
  order: number;
  /** A hero whose idle sprite "moves in" to this zone once the item is built. */
  residentHero?: string;
}

// Costs ramp gently: ~80–120 coins/session, so the first 2–3 land in early sessions and
// the big roof showpieces take 2–3 sessions to save for.
export const HQ_ITEMS: HqItem[] = [
  { id: 'lobby_sofa', name: 'Lounge sofa', zone: 'lobby', art: '🛋️', cost: 60, order: 1 },
  { id: 'lobby_plant', name: 'Big plant', zone: 'lobby', art: '🪴', cost: 90, order: 2 },
  { id: 'gym_weights', name: 'Training weights', zone: 'gym', art: '🏋️', cost: 130, order: 3 },
  { id: 'gym_bag', name: 'Punching bag', zone: 'gym', art: '🥊', cost: 180, order: 4 },
  { id: 'lab_micro', name: 'Microscope', zone: 'lab', art: '🔬', cost: 250, order: 5 },
  { id: 'lab_computer', name: 'Super-computer', zone: 'lab', art: '🖥️', cost: 330, order: 6, residentHero: 'iron-man' },
  { id: 'garage_bike', name: 'Hero cycle', zone: 'garage', art: '🏍️', cost: 450, order: 7 },
  { id: 'garage_car', name: 'Hero car', zone: 'garage', art: '🚗', cost: 600, order: 8 },
  { id: 'roof_dish', name: 'Satellite dish', zone: 'roof', art: '🛰️', cost: 800, order: 9 },
  { id: 'roof_pad', name: 'Launch pad', zone: 'roof', art: '🚀', cost: 1000, order: 10, residentHero: 'thor' },
  { id: 'roof_jet', name: 'Quinjet', zone: 'roof', art: '🛸', cost: 1300, order: 11 },
];

/** Heroes always present, independent of any build (the base is never empty). */
export const PERMANENT_RESIDENTS: { zone: HqZone; heroId: string }[] = [
  { zone: 'lobby', heroId: 'spidey' },
];

export function itemById(id: string): HqItem | undefined {
  return HQ_ITEMS.find((i) => i.id === id);
}

export function zonesInOrder(): HqZoneDef[] {
  return ZONES;
}

/** Items in a zone, cheapest first (so a zone fills bottom-up as he affords more). */
export function itemsInZone(zone: HqZone): HqItem[] {
  return HQ_ITEMS.filter((i) => i.zone === zone).sort((a, b) => a.cost - b.cost || a.order - b.order);
}

/** The default "next goal": the cheapest item he hasn't built yet (null if all built). */
export function cheapestUnowned(owned: string[]): HqItem | null {
  const set = new Set(owned);
  const remaining = HQ_ITEMS.filter((i) => !set.has(i.id)).sort((a, b) => a.cost - b.cost || a.order - b.order);
  return remaining[0] ?? null;
}
